import "./SearchHistory.scss";
import React, { useEffect, useState } from "react";
import { Box } from "../../../components/styled-system/Box";
import { Spinner } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { fetchSearchHistoryStatistics, downloadSearchHistory } from "../../../api/old-vine-registry-api";
import { SearchHistoryStatistics } from "../../../api/models";

const COLORS = [
  "#184472", "#2e7d32", "#e65100", "#6a1b9a", "#c62828",
  "#00838f", "#ef6c00", "#4527a0", "#1b5e20", "#ad1457",
];

const renderLabel = (entry: any) =>
  `${entry.name} (${entry.value})`;

export const SearchHistoryTable: React.FC = () => {
  const [stats, setStats] = useState<SearchHistoryStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSearchHistoryStatistics()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Old Vine Search History";
    return () => { document.title = "Old Vine Registry"; };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Spinner animation="border" role="status" />
      </Box>
    );
  }

  const topSearchesThisMonth = stats?.topSearchesThisMonth || [];
  const topSearchesThisYear = stats?.topSearchesThisYear || [];
  const noResultSearches = stats?.noResultSearches || [];
  const monthlyVolume = stats?.monthlyVolume || [];
  const hourlyVolume = (stats?.hourlyVolume || []).map(h => ({
    ...h,
    hour: h.hour.substring(11), // extract "HH" from "YYYY-MM-DD HH"
  }));
  const latestSearches = stats?.latestSearches || [];

  return (
    <div className="admin-panel">
      {/* Pie charts row */}
      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "32px" }}>
        {/* Top 10 This Month */}
        <div style={{ flex: "1", minWidth: "340px" }}>
          <h3 className="title-small">Top 10 Searches — This Month</h3>
          {topSearchesThisMonth.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={topSearchesThisMonth}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  startAngle={90}
                  endAngle={-270}
                  label={renderLabel}
                >
                  {topSearchesThisMonth.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="description-text" style={{ marginTop: "16px" }}>No searches this month.</p>
          )}
        </div>

        {/* Top 10 This Year */}
        <div style={{ flex: "1", minWidth: "340px" }}>
          <h3 className="title-small">Top 10 Searches — This Year</h3>
          {topSearchesThisYear.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={topSearchesThisYear}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  startAngle={90}
                  endAngle={-270}
                  label={renderLabel}
                >
                  {topSearchesThisYear.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="description-text" style={{ marginTop: "16px" }}>No searches this year.</p>
          )}
        </div>

        {/* No Results pie */}
        <div style={{ flex: "1", minWidth: "340px" }}>
          <h3 className="title-small">Latest 10 No-Result Searches</h3>
          {noResultSearches.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={noResultSearches}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name }) => name}
                >
                  {noResultSearches.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="description-text" style={{ marginTop: "16px" }}>No zero-result searches found.</p>
          )}
        </div>
      </div>

      {/* Bar charts row */}
      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "32px" }}>
        {/* Monthly volume bar chart */}
        {monthlyVolume.length > 0 && (
          <div style={{ flex: "1", minWidth: "400px" }}>
            <h3 className="title-small">Monthly Search Volume — {new Date().getFullYear()}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#184472" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Hourly volume bar chart — last 24 hours */}
        {hourlyVolume.length > 0 && (
          <div style={{ flex: "1", minWidth: "400px" }}>
            <h3 className="title-small">Hourly Search Volume — Last 24 Hours</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" fontSize={12} label={{ value: "Hour", position: "insideBottom", offset: -5 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Last 20 searches table */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="title-small" style={{ margin: 0 }}>Last 20 Searches</h3>
        <button className="button" onClick={() => downloadSearchHistory()} style={{ whiteSpace: "nowrap" }}>
          Download Search History
        </button>
      </div>
      <Table hover size="sm" className="vineyard-table input-text u-mt-large">
        <thead>
          <tr>
            <th>Date</th>
            <th>Search String</th>
            <th>Records</th>
          </tr>
        </thead>
        <tbody>
          {latestSearches.map(row => (
            <tr key={row.id}>
              <td>{new Date(row.date!).toLocaleString()}</td>
              <td>{row.searchString}</td>
              <td>{row.resultCount}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
